import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  /** Объясняет, что сделать дальше. Пустой экран без подсказки — тупик. */
  description?: string;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      {icon && (
        <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-surface-2 text-subtle">
          {icon}
        </div>
      )}
      <h3 className="text-[15px] font-semibold">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-muted">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

interface ProgressBarProps {
  /** Значение 0–1. Больше единицы отображается как переполнение. */
  value: number;
  accent?: string;
  height?: 'sm' | 'md';
  className?: string;
  label?: string;
}

export function ProgressBar({
  value,
  accent,
  height = 'md',
  className,
  label,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const overflow = value > 1;

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-full bg-surface-3',
        height === 'sm' ? 'h-1.5' : 'h-2.5',
        className,
      )}
      role="progressbar"
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      {/* Анимируется `transform`, а не ширина: изменение ширины заставляет
          браузер пересчитывать раскладку на каждом кадре, а на экране недели
          таких полос семь, плюс по одной на каждый курс лекарств. */}
      <div
        className="h-full w-full origin-left rounded-full transition-[transform,background-color] duration-500 ease-[var(--ease-out-soft)]"
        style={{
          transform: `scaleX(${clamped})`,
          backgroundColor: overflow ? 'var(--kairo-danger)' : (accent ?? 'var(--kairo-brand)'),
        }}
      />
    </div>
  );
}

/** Заглушка на время загрузки. Держит layout, чтобы контент не «прыгал». */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-[var(--animate-shimmer)] rounded-md bg-surface-3', className)} />;
}

export function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  );
}
